import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Paperclip, Trash2, PlusCircle } from "lucide-react";
import apiClient from "@/api/client";
import { formatAmount } from "@/utils/formatAmount";
import { InvoiceAiBadge, InvoiceAiPanel, type InvoiceAiAnalysis } from "@/features/invoices/components/InvoiceAiBadge";
import { WhitelistBadge, type WhitelistCheck } from "@/features/invoices/components/WhitelistBadge";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { DatePicker } from "@/components/ui/DatePicker";
import { ManagedFileLink, type ScanStatus } from "@/components/ui/ManagedFileLink";
import { Select } from "@/components/ui/Select";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { usePageTitle } from "@/hooks/usePageTitle";

// ── Types ──────────────────────────────────────────────────────────────────────

interface PaymentEvent {
  id: string;
  amount_gross: string;
  payment_date: string;
  comment: string;
  recorded_by_name: string;
  created_at: string;
}

interface InvoiceDetail {
  id: string;
  protocol: string;
  protocol_number: string;
  order_number: string;
  order_id: string | null;
  invoice_number: string;
  invoice_managed_file: string | null;
  invoice_managed_file_detail: { id: string; original_filename: string; content_type: string; size_bytes: number; url: string | null; scan_status?: ScanStatus } | null;
  amount_net: string;
  amount_gross: string;
  contractor_is_vat_payer: boolean;
  contractor_entity_type?: "company" | "individual";
  document_label?: string;
  mandate_type?: string;
  mandate_type_label?: string;
  submitted_by: string;
  submitted_by_name: string;
  submitted_at: string;
  status: string;
  status_comment: string;
  booking_status: string;
  payment_status: string;
  paid_amount: string;
  payment_comment: string;
  is_advance_payment: boolean;
  payment_events: PaymentEvent[];
  ai_analysis?: InvoiceAiAnalysis | null;
  whitelist_check?: WhitelistCheck | null;
  created_at: string;
  updated_at: string;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const statusColor: Record<string, "gray" | "green" | "red" | "blue"> = {
  submitted: "blue", accepted: "green", rejected: "red",
};
const statusLabel: Record<string, string> = {
  submitted: "Przesłana", accepted: "Przyjęta", rejected: "Nieprzyjęta",
};
const paymentColor: Record<string, "gray" | "green" | "yellow" | "blue"> = {
  unpaid: "gray", partially_paid: "yellow", transfer_prepared: "blue", paid: "green",
};
const paymentLabel: Record<string, string> = {
  unpaid: "Nieopłacona", partially_paid: "Opłacona częściowo", transfer_prepared: "Przelew przygotowany", paid: "Opłacona",
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

// ── Page ───────────────────────────────────────────────────────────────────────

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { user } = useAuthStore();
  const canManage = user?.role === "director" || user?.role === "accountant";

  const { data: invoice, isLoading } = useQuery({
    queryKey: ["invoices", id],
    queryFn: () =>
      apiClient.get<{ data: InvoiceDetail }>(`/invoices/${id}/`).then((r) => r.data.data),
    enabled: !!id,
  });
  usePageTitle(invoice && `Faktura ${invoice.invoice_number}`);

  // ── Status / booking modals ──
  const [modalType, setModalType] = useState<"status" | "booking" | null>(null);
  const [form, setForm] = useState({ status: "", booking_status: "", comment: "" });

  const openModal = (type: "status" | "booking") => {
    if (!invoice) return;
    setModalType(type);
    setForm({ status: invoice.status, booking_status: invoice.booking_status, comment: "" });
  };

  const actionMutation = useMutation({
    mutationFn: ({ action, payload }: { action: string; payload: unknown }) =>
      apiClient.patch(`/invoices/${id}/${action}/`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices", id] });
      setModalType(null);
    },
  });

  const analyzeMutation = useMutation({
    mutationFn: () => apiClient.post(`/invoices/${id}/analyze/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invoices", id] }),
  });

  // ── Payment events ──
  const [addingPayment, setAddingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount_gross: "", payment_date: todayIso(), comment: "" });
  const [paymentError, setPaymentError] = useState("");

  const addPaymentMutation = useMutation({
    mutationFn: (payload: unknown) => apiClient.post(`/invoices/${id}/add-payment/`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices", id] });
      setAddingPayment(false);
      setPaymentForm({ amount_gross: "", payment_date: todayIso(), comment: "" });
      setPaymentError("");
    },
    onError: () => setPaymentError("Nie udało się zarejestrować płatności."),
  });

  const deletePaymentMutation = useMutation({
    mutationFn: (paymentId: string) =>
      apiClient.delete(`/invoices/${id}/payments/${paymentId}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invoices", id] }),
  });

  const handleAddPayment = () => {
    if (!paymentForm.amount_gross || isNaN(parseFloat(paymentForm.amount_gross))) {
      setPaymentError("Kwota brutto jest wymagana.");
      return;
    }
    if (!paymentForm.payment_date) {
      setPaymentError("Data płatności jest wymagana.");
      return;
    }
    setPaymentError("");
    addPaymentMutation.mutate({
      amount_gross: paymentForm.amount_gross,
      payment_date: paymentForm.payment_date,
      comment: paymentForm.comment,
    });
  };

  if (isLoading) return <PageLoader />;
  if (!invoice) return <p className="text-gray-500 dark:text-gray-400">Nie znaleziono dokumentu.</p>;
  const docLabel = invoice.document_label ?? "Faktura";
  const docLabelLower = docLabel.toLowerCase();

  const vatRate = invoice.contractor_is_vat_payer ? 1.23 : 1;
  const totalPaidGross = invoice.payment_events.reduce((s, p) => s + parseFloat(p.amount_gross), 0);
  const totalPaidNet = totalPaidGross / vatRate;
  const remainingNet = parseFloat(invoice.amount_net) - totalPaidNet;
  const paidPct = parseFloat(invoice.amount_gross) > 0
    ? ((totalPaidGross / parseFloat(invoice.amount_gross)) * 100).toFixed(1)
    : "0.0";
  // Podwykonawca ogląda tę samą stronę faktury, ale zamówienie ma pod własną trasą —
  // wewnętrzne /orders/:id pokazałoby mu widok GK (stawki, marże).
  const orderPath =
    user?.role === "contractor"
      ? `/contractor/orders/${invoice.order_id}`
      : `/orders/${invoice.order_id}`;

  return (
    <div className="max-w-3xl">
      {/* Back */}
      <Link
        to={`/protocols/${invoice.protocol}`}
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 mb-5"
      >
        <ArrowLeft size={14} /> Powrót do protokołu {invoice.protocol_number}
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 inline-flex items-center gap-2">
              {invoice.invoice_number}
              <InvoiceAiBadge analysis={invoice.ai_analysis} />
            </h1>
            <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Zamówienie:{" "}
            {invoice.order_id ? (
              <Link to={orderPath} className="text-[var(--color-primary)] hover:underline">
                {invoice.order_number}
              </Link>
            ) : (
              invoice.order_number
            )}
            {" · "}
            POCK:{" "}
            <Link to={`/protocols/${invoice.protocol}`} className="text-[var(--color-primary)] hover:underline">
              {invoice.protocol_number}
            </Link>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Badge color={statusColor[invoice.status] ?? "gray"}>
            {statusLabel[invoice.status] ?? invoice.status}
          </Badge>
          <Badge color={invoice.booking_status === "booked" ? "green" : "gray"}>
            {invoice.booking_status === "booked" ? "Zaksięgowana" : "Niezaksięgowana"}
          </Badge>
          <Badge color={paymentColor[invoice.payment_status] ?? "gray"}>
            {paymentLabel[invoice.payment_status] ?? invoice.payment_status}
          </Badge>
          {invoice.is_advance_payment && <Badge color="yellow">Zaliczkowa</Badge>}
          <WhitelistBadge check={invoice.whitelist_check} />
        </div>
      </div>

      {invoice.contractor_entity_type !== "individual" && (
        <div className="mb-6">
          {invoice.ai_analysis && <InvoiceAiPanel analysis={invoice.ai_analysis} />}
          <div className={invoice.ai_analysis ? "mt-2" : ""}>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => analyzeMutation.mutate()}
              loading={analyzeMutation.isPending}
            >
              {invoice.ai_analysis ? "Analizuj ponownie (AI)" : "Uruchom analizę AI"}
            </Button>
          </div>
        </div>
      )}

      {/* Action bar */}
      {canManage && (
        <div className="flex items-center gap-2 mb-6">
          <Button size="sm" variant="secondary" onClick={() => openModal("status")}>Zmień status</Button>
          <Button size="sm" variant="secondary" onClick={() => openModal("booking")}>Zmień zaksięgowanie</Button>
          {invoice.status === "accepted" && (invoice.payment_status === "unpaid" || invoice.payment_status === "partially_paid") && (
            <Button
              size="sm"
              variant="secondary"
              loading={actionMutation.isPending}
              onClick={() => actionMutation.mutate({ action: "update_payment", payload: { payment_status: "transfer_prepared" } })}
            >
              Przelew przygotowany
            </Button>
          )}
        </div>
      )}

      {/* Financial details */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <InfoCard label="Kwota netto" value={`${formatAmount(invoice.amount_net)} PLN`} />
        <InfoCard label="Kwota brutto" value={`${formatAmount(invoice.amount_gross)} PLN`} />
        {invoice.contractor_is_vat_payer && (
          <InfoCard
            label="VAT (23%)"
            value={`${formatAmount(String(parseFloat(invoice.amount_gross) - parseFloat(invoice.amount_net)))} PLN`}
          />
        )}
        <InfoCard
          label="Opłacono (brutto)"
          value={`${formatAmount(String(totalPaidGross))} PLN (${paidPct}%)`}
        />
        {remainingNet > 0.005 && (
          <InfoCard
            label="Pozostało do zapłaty (netto)"
            value={`${formatAmount(String(remainingNet))} PLN`}
            highlight
          />
        )}
      </div>

      {/* Status comment */}
      {invoice.status_comment && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <p className="text-xs font-semibold text-amber-700 mb-1">Komentarz statusu</p>
          <p className="text-sm text-amber-800">{invoice.status_comment}</p>
        </div>
      )}

      {/* Submission info */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <InfoCard label="Przesłana przez" value={invoice.submitted_by_name} />
        <InfoCard
          label="Data przesłania"
          value={invoice.submitted_at ? new Date(invoice.submitted_at).toLocaleDateString("pl") : "—"}
        />
      </div>

      {/* File */}
      <div className="bg-white dark:bg-gray-800 p-4 mb-6">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Plik/wydruk KSeF</p>
        <ManagedFileLink
          file={invoice.invoice_managed_file_detail}
          icon={<Paperclip size={14} />}
          iconSize={14}
          className="!text-sm gap-2"
          empty={
            <p className="text-sm text-gray-400 dark:text-gray-500">Brak pliku {docLabelLower}</p>
          }
        >
          Pobierz plik {docLabelLower}
        </ManagedFileLink>
      </div>

      {/* Payment history */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 border-l-4 border-[var(--color-primary)] pl-3">
            Historia płatności
          </h2>
          {canManage && !addingPayment && (
            <Button variant="secondary" onClick={() => setAddingPayment(true)}>
              <PlusCircle size={15} />
              Zarejestruj płatność
            </Button>
          )}
        </div>

        {/* Add payment form */}
        {addingPayment && (
          <div className="bg-white dark:bg-gray-800 p-5 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                  Kwota zapłacona brutto (PLN)<span className="text-red-500 ml-0.5">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentForm.amount_gross}
                  onChange={(e) => setPaymentForm((f) => ({ ...f, amount_gross: e.target.value }))}
                  placeholder="0.00"
                  className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                />
                {paymentForm.amount_gross && !isNaN(parseFloat(paymentForm.amount_gross)) && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    = {formatAmount(String(parseFloat(paymentForm.amount_gross) / vatRate))} PLN netto
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                  Data płatności<span className="text-red-500 ml-0.5">*</span>
                </label>
                <DatePicker
                  value={paymentForm.payment_date}
                  onChange={(v) => setPaymentForm((f) => ({ ...f, payment_date: v }))}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                  Komentarz
                </label>
                <input
                  type="text"
                  value={paymentForm.comment}
                  onChange={(e) => setPaymentForm((f) => ({ ...f, comment: e.target.value }))}
                  placeholder="Opcjonalnie"
                  className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                />
              </div>
            </div>
            {paymentError && <p className="text-xs text-red-600 mb-3">{paymentError}</p>}
            <div className="flex gap-3">
              <Button onClick={handleAddPayment} loading={addPaymentMutation.isPending}>
                Zapisz płatność
              </Button>
              <Button
                variant="secondary"
                onClick={() => { setAddingPayment(false); setPaymentForm({ amount_gross: "", payment_date: todayIso(), comment: "" }); setPaymentError(""); }}
                disabled={addPaymentMutation.isPending}
              >
                Anuluj
              </Button>
            </div>
          </div>
        )}

        {/* Payment events list */}
        {invoice.payment_events.length > 0 ? (
          <div className="bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[400px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Data</th>
                  <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Kwota brutto</th>
                  <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Netto</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Komentarz</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zapisał</th>
                  {canManage && <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {invoice.payment_events.map((pmt) => {
                  const gross = parseFloat(pmt.amount_gross);
                  const net = gross / vatRate;
                  return (
                    <tr key={pmt.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-2 border-transparent hover:border-[var(--color-primary)] transition-colors">
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {new Date(pmt.payment_date).toLocaleDateString("pl")}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-gray-100">
                        {formatAmount(pmt.amount_gross)} PLN
                      </td>
                      <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">
                        {formatAmount(String(net))} PLN
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                        {pmt.comment || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                        {pmt.recorded_by_name || "—"}
                      </td>
                      {canManage && (
                        <td className="px-4 py-3">
                          <button
                            onClick={() => deletePaymentMutation.mutate(pmt.id)}
                            disabled={deletePaymentMutation.isPending}
                            className="text-red-500 hover:text-red-700 dark:hover:text-red-400"
                            title="Usuń płatność"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="border-t-2 border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 font-semibold text-sm">
                <tr>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Razem</td>
                  <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">
                    {formatAmount(String(totalPaidGross))} PLN
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                    {formatAmount(String(totalPaidNet))} PLN
                  </td>
                  <td colSpan={canManage ? 3 : 2} />
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          !addingPayment && (
            <p className="text-sm text-gray-500 dark:text-gray-400">Brak zarejestrowanych płatności.</p>
          )
        )}
      </section>

      {/* Status modal */}
      <Modal open={modalType === "status"} onClose={() => setModalType(null)} title={`Zmień status ${docLabelLower}`} size="sm">
        <div className="flex flex-col gap-4">
          <Select
            label="Status"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            options={[
              { value: "submitted", label: "Przesłana" },
              { value: "accepted", label: "Przyjęta" },
              { value: "rejected", label: "Nieprzyjęta" },
            ]}
          />
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Komentarz</label>
            <textarea
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm dark:bg-gray-700 dark:text-gray-100"
              rows={2}
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
            />
          </div>
          <Button
            loading={actionMutation.isPending}
            onClick={() => actionMutation.mutate({ action: "update_status", payload: { status: form.status, status_comment: form.comment } })}
          >
            Zapisz
          </Button>
        </div>
      </Modal>

      {/* Booking modal */}
      <Modal open={modalType === "booking"} onClose={() => setModalType(null)} title="Zmień status zaksięgowania" size="sm">
        <div className="flex flex-col gap-4">
          <Select
            label="Status zaksięgowania"
            value={form.booking_status}
            onChange={(e) => setForm({ ...form, booking_status: e.target.value })}
            options={[
              { value: "not_booked", label: "Niezaksięgowana" },
              { value: "booked", label: "Zaksięgowana" },
            ]}
          />
          <Button
            loading={actionMutation.isPending}
            onClick={() => actionMutation.mutate({ action: "update_booking", payload: { booking_status: form.booking_status } })}
          >
            Zapisz
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function InfoCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`p-4 ${highlight ? "bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700" : "bg-white dark:bg-gray-800"}`}>
      <p className={`text-xs mb-1 ${highlight ? "text-amber-600 dark:text-amber-400" : "text-gray-500 dark:text-gray-400"}`}>{label}</p>
      <p className={`font-medium ${highlight ? "text-amber-800 dark:text-amber-300" : "text-gray-900 dark:text-gray-100"}`}>{value}</p>
    </div>
  );
}
